import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useI18n } from "../I18nContext";

const STATUS_FILTERS = ["pending", "approved", "rejected"];

const ROLE_I18N = {
  entrepreneur: "auth.roleEntrepreneur",
  advisor: "auth.roleAdvisor",
  provider: "auth.roleProvider",
  investor: "auth.roleInvestor",
  accelerator: "auth.roleAccelerator",
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso.slice(0, 16); }
}

export default function WaitlistTab() {
  const { t } = useI18n();
  const [mode, setMode] = useState("waitlist");
  const [modeSaving, setModeSaving] = useState(false);
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadMode = async () => {
    try {
      const r = await api.get("/admin/signup-mode");
      setMode(r.data?.mode || "waitlist");
    } catch (e) {
      /* non-fatal */
    }
  };

  const loadList = async (nextStatus = status) => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/admin/waitlist?status=${encodeURIComponent(nextStatus)}`);
      setItems(r.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMode(); loadList("pending"); }, []);

  const changeMode = async (nextMode) => {
    if (nextMode === mode) return;
    setModeSaving(true);
    setErr("");
    try {
      await api.post("/admin/signup-mode", { mode: nextMode });
      setMode(nextMode);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setModeSaving(false);
    }
  };

  const approve = async (id) => {
    if (!window.confirm(t("adminWaitlist.confirmApprove"))) return;
    setBusyId(id);
    setErr("");
    try {
      await api.post(`/admin/waitlist/${id}/approve`);
      await loadList();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm(t("adminWaitlist.confirmReject"))) return;
    setBusyId(id);
    setErr("");
    try {
      await api.post(`/admin/waitlist/${id}/reject`);
      await loadList();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const switchStatus = (s) => { setStatus(s); loadList(s); };

  return (
    <div data-testid="admin-waitlist-tab">
      <div className="tb-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t("adminWaitlist.modeTitle")}</h3>
        <p className="tb-muted" style={{ marginTop: 4 }}>
          {t("adminWaitlist.modeHelp")}
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            className={`tb-ghost-btn${mode === "waitlist" ? " on" : ""}`}
            onClick={() => changeMode("waitlist")}
            disabled={modeSaving}
            data-testid="admin-signup-mode-waitlist"
            style={mode === "waitlist" ? { background: "var(--accent)", color: "#021018", borderColor: "var(--accent)" } : {}}
          >
            {t("adminWaitlist.modeWaitlist")}
          </button>
          <button
            className={`tb-ghost-btn${mode === "open" ? " on" : ""}`}
            onClick={() => changeMode("open")}
            disabled={modeSaving}
            data-testid="admin-signup-mode-open"
            style={mode === "open" ? { background: "var(--accent)", color: "#021018", borderColor: "var(--accent)" } : {}}
          >
            {t("adminWaitlist.modeOpen")}
          </button>
          <span className="tb-muted" data-testid="admin-signup-mode-current">
            {t("adminWaitlist.modeCurrent", { mode: t(`adminWaitlist.mode_${mode}`) })}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            className={`tb-ghost-btn${status === s ? " on" : ""}`}
            onClick={() => switchStatus(s)}
            data-testid={`admin-waitlist-filter-${s}`}
            style={status === s ? { background: "var(--accent)", color: "#021018", borderColor: "var(--accent)" } : {}}
          >
            {t(`adminWaitlist.status_${s}`)}
          </button>
        ))}
        <button
          className="tb-ghost-btn"
          onClick={() => loadList()}
          disabled={loading}
          data-testid="admin-waitlist-refresh"
          style={{ marginLeft: "auto" }}
        >
          {loading ? "…" : `⟳ ${t("adminConsole.refresh")}`}
        </button>
      </div>

      {err && <div className="tb-form-error" data-testid="admin-waitlist-error" style={{ marginBottom: 12 }}>{err}</div>}

      {loading ? (
        <p className="tb-muted">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="tb-card"><p className="tb-muted" data-testid="admin-waitlist-empty">{t("adminWaitlist.empty")}</p></div>
      ) : (
        <div className="tb-card" style={{ overflowX: "auto" }}>
          <table className="tb-mem-audit-table" data-testid="admin-waitlist-table">
            <thead>
              <tr>
                <th>{t("adminWaitlist.col.name")}</th>
                <th>{t("adminWaitlist.col.email")}</th>
                <th>{t("adminWaitlist.col.role")}</th>
                <th>{t("adminWaitlist.col.company")}</th>
                <th>{t("adminWaitlist.col.note")}</th>
                <th>{t("adminWaitlist.col.created")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} data-testid={`admin-waitlist-row-${w.id}`}>
                  <td><strong>{w.name}</strong></td>
                  <td>{w.email}</td>
                  <td>{t(ROLE_I18N[w.role] || w.role)}</td>
                  <td>{w.company || "—"}</td>
                  <td style={{ maxWidth: 260 }}>{w.note ? <span title={w.note}>{w.note.slice(0, 100)}{w.note.length > 100 ? "…" : ""}</span> : <span className="tb-muted">—</span>}</td>
                  <td className="tb-muted"><small>{fmtDate(w.created_at)}</small></td>
                  <td>
                    {status === "pending" ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          type="button"
                          className="tb-primary-btn tb-mini"
                          onClick={() => approve(w.id)}
                          disabled={busyId === w.id}
                          data-testid={`admin-waitlist-approve-${w.id}`}
                        >
                          {busyId === w.id ? "…" : t("adminWaitlist.approve")}
                        </button>
                        <button
                          type="button"
                          className="tb-ghost-btn tb-mini"
                          onClick={() => reject(w.id)}
                          disabled={busyId === w.id}
                          data-testid={`admin-waitlist-reject-${w.id}`}
                        >
                          {t("adminWaitlist.reject")}
                        </button>
                      </div>
                    ) : (
                      <span className="tb-muted"><small>{fmtDate(w.approved_at || w.rejected_at)}</small></span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
